import { useLocation, useParams } from 'react-router-dom';
import instance from '../config/axios';
import { useEffect, useState } from 'react';
import { Prescription, Product, ROLE } from '../interface/interface';
import { useAuth } from 'react-oidc-context';
import Loading from '../components/Loading';
import formatePriceByCost from '../utils/formatPriceByCost';
import ProductCarousel from '../components/ProductCarousel';
import RenderProductDescription from '../components/RenderProductDescription';
import renderMediaUrls from '../utils/getMediaUrls';
import BackToPrevious from '../components/BackToPrevious';
import AddToCartButton from '../components/AddToCartButton';
import GuestAddToCartButton from '../components/GuestAddToCartButton';
import { GuestCartProvider } from '../context/guestCartContext';
import { PrescriptionDetail } from '../components/PrescriptionDetails';
import moment from 'moment';
import CustomModal from '../components/CustomModal';
import { MAX_ZOOM, MIN_ZOOM, ZOOM_STEP } from '../constant/constant';
import useHandleZoom from '../hooks/usehandleZoom';
import { ArrowLeftIcon } from '../assets/svgImages';

interface ProductDetailsProps {
  isGuest?: boolean;
}

const ProductDetails = ({ isGuest = false }: ProductDetailsProps) => {
  const [product, setProduct] = useState<Product | null>(null);
  const [prescription, setPrescription] = useState<Prescription | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { productId } = useParams();
  const location = useLocation();
  const [open, setOpen] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [zoom, setZoom] = useState<number>(MIN_ZOOM);
  const {
    handleTouchMove,
    handleTouchStart,
    position,
    setPosition,
    setDragging,
  } = useHandleZoom(zoom);

  const { user } = useAuth();

  const prescriptionDetails = async () => {
    if (isGuest) return;
    setLoading(true);
    try {
      const response = await instance.get(
        `api/v1/prescription/product/${productId}`,
        { headers: { Authorization: `Bearer ${user?.access_token}` } },
      );
      const responseData = response.data;

      setPrescription(responseData);

      setLoading(false);
    } catch (err) {
      setLoading(false);
      setPrescription(null);
      console.error(
        `Error at get ${isGuest ? 'Guest ' : ''} Product Details`,
        err,
      );
    }
  };

  const productDetails = async () => {
    setLoading(true);
    try {
      const endpoint = isGuest
        ? `/guest/product/${productId}`
        : `api/v1/product/${productId}`;

      const headers = isGuest
        ? {}
        : { Authorization: `Bearer ${user?.access_token}` };

      const response = await instance.get(endpoint, { headers });
      const responseData = response.data;

      setProduct(responseData);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(
        `Error at get ${isGuest ? 'Guest ' : ''}Product Details`,
        err,
      );
    }
  };

  useEffect(() => {
    if (!isGuest && !user?.access_token) {
      console.error('User Access Token not found', user?.access_token);
      return;
    }
    productDetails();
    prescriptionDetails();
  }, [isGuest, user?.access_token, productId]);

  if (loading) return <Loading />;

  return (
    <>
      <BackToPrevious />
      {product ? (
        <div className="bg-white rounded-md shadow p-5">
          <div className="md:flex justify-between">
            <div className="flex items-center">
              <div
                onClick={() => window.location.replace(location?.state?.from)}
                className="cursor-pointer me-4"
              >
                <ArrowLeftIcon />
              </div>
              <h1 className="text-3xl 2xl:text-4xl font-semibold text-primary-blue-500 uppercase">
                {product.name}
              </h1>
            </div>
            {(user?.profile?.groups as Array<string>)?.includes(
              ROLE.Patients,
            ) ? (
              <AddToCartButton
                product={product}
                maxQty={location?.state?.totalQty}
                substituteProduct={location?.state?.substituteProduct}
              />
            ) : (
              <></>
            )}
            {isGuest ? (
              <GuestCartProvider>
                <GuestAddToCartButton
                  product={product}
                  substituteProduct={product.substituteProduct}
                />
              </GuestCartProvider>
            ) : (
              <></>
            )}
          </div>
          {product.prescription_required ? (
            <></>
          ) : (
            <div className="mt-3">
              <label className="text-primary-blue-500 font-semibold text-base">
                Price
              </label>
              <p className="font-regular text-sm text-black mt-1">
                ${product?.price || '-'}
              </p>
            </div>
          )}
          <div className="mt-3">
            <label className="text-primary-blue-500 font-semibold text-base">
              Description
            </label>
            <p className="font-regular text-sm text-black mt-1">
              <RenderProductDescription description={product.description} />
            </p>
          </div>

          <div className="flex justify-between items-start flex-col-reverse md:flex-row">
            {product.prescription_required ? (
              <div className="mt-6 w-full md:w-1/2">
                <dl className="divide-y divide-gray-100">
                  <div className="px-4 py-6 grid grid-cols-2 gap-4 px-0">
                    <dt className="text-primary-blue-500 font-semibold text-base">
                      Brand
                    </dt>
                    <dd className="mt-1 text-sm leading-6 text-gray-700 col-span-1 mt-0 text-right uppercase">
                      {product.product_brand || '-'}
                    </dd>
                  </div>
                  <div className="px-4 py-6 grid grid-cols-2 gap-4 px-0">
                    <dt className="text-primary-blue-500 font-semibold text-base">
                      Day / Night
                    </dt>
                    <dd className="mt-1 text-sm leading-6 text-gray-700 col-span-1 mt-0 text-right uppercase">
                      {product?.dayOrNight?.name || '-'}
                    </dd>
                  </div>
                  <div className="px-4 py-6 grid grid-cols-2 gap-4 px-0 items-start">
                    <dt className="text-primary-blue-500 font-semibold text-base">
                      Strength (CBD:THC)
                    </dt>

                    <dd className="text-sm leading-6 text-gray-700 text-right break-words whitespace-normal">
                      {product?.product_strength || '-'}
                    </dd>
                  </div>
                  <div className="px-4 py-6 grid grid-cols-2 gap-4 px-0">
                    <dt className="text-primary-blue-500 font-semibold text-base">
                      Category
                    </dt>
                    <dd className="mt-1 text-sm leading-6 text-gray-700 col-span-1 mt-0 text-right">
                      {product?.productCategory?.name || '-'}
                    </dd>
                  </div>
                  <div className="px-4 py-6 grid grid-cols-2 gap-4 px-0">
                    <dt className="text-primary-blue-500 font-semibold text-base">
                      Formulation
                    </dt>
                    <dd className="mt-1 text-sm leading-6 text-gray-700 col-span-1 mt-0 text-right">
                      {product?.productFormulation?.name || '-'}
                    </dd>
                  </div>
                  <div className="px-4 py-6 grid grid-cols-2 gap-4 px-0">
                    <dt className="text-primary-blue-500 font-semibold text-base">
                      TGA
                    </dt>
                    <dd className="mt-1 text-sm leading-6 text-gray-700 col-span-1 mt-0 text-right">
                      {product?.tgaCategory?.short_name || '-'}
                    </dd>
                  </div>
                  {/* Only show stock on hand for authenticated users who are not patients */}
                  {!isGuest &&
                    !(user?.profile.groups as string[]).includes(
                      'Patients',
                    ) && (
                      <div className="px-4 py-6 grid grid-cols-2 gap-4 px-0">
                        <dt className="text-primary-blue-500 font-semibold text-base">
                          Stock on Hand
                        </dt>
                        <dd className="mt-1 text-sm leading-6 text-gray-700 col-span-1 mt-0 text-right">
                          {product?.inventory_quantity || '-'}
                        </dd>
                      </div>
                    )}
                  <div className="px-4 py-6 grid grid-cols-2 gap-4 px-0">
                    <dt className="text-primary-blue-500 font-semibold text-base">
                      Price
                    </dt>
                    <dd className="mt-1 text-sm leading-6 text-gray-700 col-span-1 mt-0 text-right">
                      ${product?.price || '-'}
                    </dd>
                  </div>
                  <div className="px-4 py-6 grid grid-cols-2 gap-4 px-0">
                    <dt className="text-primary-blue-500 font-semibold text-base">
                      Cost Per Unit
                    </dt>
                    <dd className="mt-1 text-sm leading-6 text-gray-700 col-span-1 mt-0 text-right">
                      {formatePriceByCost(
                        Number(product?.price),
                        product?.unit,
                        product?.weight,
                      )}
                    </dd>
                  </div>
                </dl>
              </div>
            ) : (
              <></>
            )}
            <div className="w-full md:w-1/2 flex justify-center z-10">
              <ProductCarousel
                media={renderMediaUrls(product)}
                setImageUrl={setImageUrl}
                setOpen={setOpen}
              />
            </div>
          </div>

          {!isGuest &&
          product.prescription_required &&
          !location?.state?.isSubstitute &&
          !location?.state?.nonOrderable &&
          (user?.profile?.groups as Array<string>)?.includes(ROLE.Patients) ? (
            <div
              className="col-span-1 xl:col-span-2 bg-secondary-pink-50 border-r border-secondary-pink-100 rounded-l-md 
                      px-3.5 xl:px-7 py-3 flex flex-col justify-between"
            >
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-base xl:text-lg text-primary-blue-500 font-bold">
                  Prescription Details
                </h3>
              </div>

              <div className="text-secondary-pink-700">
                <PrescriptionDetail
                  label="Medication Name:"
                  value={prescription?.product?.name ?? '-'}
                />
                <PrescriptionDetail
                  label="Brand Name:"
                  value={prescription?.product?.product_brand ?? '-'}
                />
                <PrescriptionDetail
                  label="Prescription Expiry:"
                  value={
                    prescription?.prescription_expiry &&
                    moment(prescription?.prescription_expiry).isValid()
                      ? `${moment(prescription?.prescription_expiry).format('DD/MM/YYYY')}`
                      : '-'
                  }
                />
                <PrescriptionDetail
                  label="Prescribed Dose:"
                  value={
                    prescription?.prescribed_dosage === ''
                      ? '-'
                      : prescription?.prescribed_dosage!
                  }
                />
                <PrescriptionDetail
                  label="Prescribed by:"
                  value={
                    prescription?.doctor
                      ? `Dr. ${prescription?.doctor?.user?.first_name} ${prescription?.doctor?.user?.last_name}`
                      : '-'
                  }
                />
                <PrescriptionDetail
                  label="Prescribed on:"
                  value={
                    prescription?.prescription_date &&
                    moment(prescription?.prescription_date).isValid()
                      ? `${moment(prescription?.prescription_date).format('DD/MM/YYYY')}`
                      : '-'
                  }
                />
                <PrescriptionDetail
                  label="Medication Form:"
                  value={prescription?.medication_formrtacid ?? '-'}
                />
                <PrescriptionDetail
                  label="Medication Strength:"
                  value={prescription?.medication_strength ?? '-'}
                />
                <PrescriptionDetail
                  label="Route Admin:"
                  value={prescription?.route_administration ?? '-'}
                />
                <PrescriptionDetail
                  label="Interval Limit"
                  value={`${prescription?.prescribed_interval?.toString() || '0'} days`}
                />
                <PrescriptionDetail
                  label="Repeats Remaining"
                  value={
                    prescription?.remaining_repeats !== undefined
                      ? prescription.remaining_repeats <= 0
                        ? 'Last Repeat'
                        : prescription.remaining_repeats.toString()
                      : '-'
                  }
                />
              </div>
            </div>
          ) : (
            <></>
          )}

          <div className="block md:hidden">
            <CustomModal
              isOpen={open}
              onClose={() => {
                setOpen(false);
                setZoom(MIN_ZOOM);
                setPosition({ x: 0, y: 0 });
              }}
              header=""
              footer=""
              modalSize="w-auto h-auto flex justify-center"
            >
              <div className="relative max-w-full max-h-[80vh] overflow-hidden flex items-center justify-center">
                <img
                  src={imageUrl}
                  alt="Preview"
                  className="object-contain max-w-full max-h-[80vh] transition-transform duration-200"
                  style={{
                    transform: `scale(${zoom}) translate(${position.x}px, ${position.y}px)`,
                    touchAction: 'none',
                  }}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={() => setDragging(false)}
                />

                <div className="absolute bottom-3 right-3 flex flex-row bg-white/90 rounded shadow">
                  <button
                    className="p-2 disabled:opacity-40"
                    onClick={() => {
                      setZoom((z) => Math.max(z - ZOOM_STEP, MIN_ZOOM));
                      setPosition({ x: 0, y: 0 });
                    }}
                    disabled={zoom <= MIN_ZOOM}
                  >
                    −
                  </button>
                  <button
                    className="p-2 border-b disabled:opacity-40"
                    onClick={() =>
                      setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM))
                    }
                    disabled={zoom >= MAX_ZOOM}
                  >
                    +
                  </button>
                </div>
              </div>
            </CustomModal>
          </div>
        </div>
      ) : (
        <h3 className="text-base font-semibold text-center leading-7 text-gray-900">
          Product Information does not exist
        </h3>
      )}
    </>
  );
};

export default ProductDetails;
